import React, { useEffect, useState } from 'react';
import { Button, SparkIcon, SparkModalPortal, Spinner } from '@spark';
import HighlightedCode from '@/features/documentation/components/HighlightedCode';
import CodeSnippetActions from '@/shared/components/CodeSnippetActions';
import { buildQueryFile } from '@/features/trainings/utils/queryFileGenerator';
import './QueryFileModal.css';

const LANGUAGE_LABELS = {
    python: 'Python',
    typescript: 'TypeScript',
    go: 'Go',
};

/**
 * Dark-themed modal showing the generated SDK query file for a task
 * (opened from the Deploy dropdown, Playground toolbar, or Models table).
 * Matches the Docs Querying code-block style: #1e1e1e surface, vs2015
 * highlighting, with Copy + Download pinned to the snippet's top-right corner.
 */
const QueryFileModal = ({
    open,
    onClose,
    language = 'python',
    clientId,
    usecaseId,
    taskName,
    projectName,
    taskType,
    usePlaygroundIngress = false,
}) => {
    const [snippet, setSnippet] = useState(null);

    useEffect(() => {
        if (!open || !usecaseId) return undefined;
        let cancelled = false;
        setSnippet(null);
        buildQueryFile({
            language,
            clientId,
            usecaseId,
            taskName,
            projectName,
            taskType,
            usePlaygroundIngress,
        }).then((result) => {
            if (!cancelled) setSnippet(result);
        });
        return () => {
            cancelled = true;
        };
    }, [open, language, clientId, usecaseId, taskName, projectName, taskType, usePlaygroundIngress]);

    const languageLabel = LANGUAGE_LABELS[language] || language;

    return (
        <SparkModalPortal open={open} onClose={onClose} size="wide">
            <div className="query-file-modal">
                <div className="query-file-modal__header">
                    <span className="query-file-modal__title fs-unmask">
                        Query File — {languageLabel}
                    </span>
                    <Button
                        variant="ghost"
                        size="small"
                        type="button"
                        aria-label="Close"
                        tooltip="Close"
                        onClick={onClose}
                        icon={<SparkIcon slug="close" size="small" />}
                        iconSlug="close"
                    />
                </div>
                <div className="query-file-modal__body">
                    {snippet === null ? (
                        <div className="query-file-modal__loading">
                            <Spinner size="medium" />
                        </div>
                    ) : (
                        <>
                            <div className="query-file-modal__toolbar">
                                <CodeSnippetActions
                                    code={snippet.content}
                                    filename={snippet.filename}
                                    variant="ghost"
                                />
                            </div>
                            <HighlightedCode code={snippet.content} language={language} />
                        </>
                    )}
                </div>
            </div>
        </SparkModalPortal>
    );
};

export default QueryFileModal;
